import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { AgendaDealAction, AgendaResponse, EventDto, GoogleStatusDto } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { fmtTime, localDay, thisMonth, todayKey, dayLabel } from '../format';
import { Empty, ErrorBanner, Field, Modal, More, Panel, Calendar, type DayMark } from '../ui';
import { VisitDetailModal, VisitFormModal } from './VisitsPage';
import { DateInput, TimeInput } from '../pickers';
import { SlotPicker, addMin, useBusy } from '../slots';
import { ask } from '../dialogs';

const REMINDERS: { v: number | null; label: string }[] = [
  { v: null, label: 'Nessuno' },
  { v: 0, label: "All'ora dell'evento" },
  { v: 10, label: '10 minuti prima' },
  { v: 30, label: '30 minuti prima' },
  { v: 60, label: '1 ora prima' },
  { v: 1440, label: '1 giorno prima' },
];

const pad = (n: number) => String(n).padStart(2, '0');

/** Giorno (YYYY-MM-DD) in cui cade l'evento: per "tutto il giorno" la data e quella salvata, altrimenti quella locale. */
const eventDay = (e: EventDto) => (e.allDay ? e.startsAt.slice(0, 10) : localDay(e.startsAt));

/** Quali appuntamenti si vedono: solo lavoro Tecnoside, solo quelli insieme a un collega, oppure tutti. */
type Show = 'tecnoside' | 'together' | 'all';
const SHOWS: { v: Show; label: string; hint: string }[] = [
  { v: 'tecnoside', label: 'Tecnoside', hint: 'Solo il lavoro Tecnoside: clienti della rubrica, trattative, visite e impegni con i colleghi' },
  { v: 'together', label: 'Insieme', hint: 'Solo gli impegni da fare insieme a un collega' },
  { v: 'all', label: 'Tutti', hint: 'Anche gli impegni di altri lavori e quelli personali' },
];
const isTogether = (e: EventDto) => (e.together?.length ?? 0) > 0;
/** Se il server non ha dato il segno (versione vecchia) l'appuntamento conta come Tecnoside. */
const isTecno = (e: EventDto) => e.tecnoside !== false;
const passes = (e: EventDto, show: Show) => (show === 'all' ? true : show === 'together' ? isTogether(e) : isTecno(e));

/** Un colore per giorno, a giro: la barra a sinistra separa a colpo d'occhio un giorno dall'altro. */
const DAY_COLORS = ['#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#ef6c2f', '#6366f1'];

/** Raggruppa gli impegni per giorno, nell'ordine in cui arrivano. */
function groupByDay<T extends { day: string }>(items: T[]): [string, T[]][] {
  const out: [string, T[]][] = [];
  for (const it of items) {
    const last = out[out.length - 1];
    if (last && last[0] === it.day) last[1].push(it);
    else out.push([it.day, [it]]);
  }
  return out;
}

function monthRange(month: string): { from: string; to: string } {
  const [y, m] = month.split('-').map(Number);
  return { from: new Date(y, m - 1, -7).toISOString(), to: new Date(y, m, 9).toISOString() };
}

const gStamp = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

/** Collegamento "Aggiungi a Google Calendar" per un singolo appuntamento. */
function googleLink(e: EventDto): string {
  let dates: string;
  if (e.allDay) {
    const s = e.startsAt.slice(0, 10);
    const n = new Date(new Date(s + 'T00:00:00Z').getTime() + 86_400_000).toISOString().slice(0, 10);
    dates = `${s.replace(/-/g, '')}/${n.replace(/-/g, '')}`;
  } else {
    const start = new Date(e.startsAt);
    const end = e.endsAt ? new Date(e.endsAt) : new Date(start.getTime() + 3_600_000);
    dates = `${gStamp(start)}/${gStamp(end)}`;
  }
  const p = new URLSearchParams({ action: 'TEMPLATE', text: e.title, dates });
  if (e.notes) p.set('details', e.notes);
  if (e.location) p.set('location', e.location);
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

/** Agenda: appuntamenti e promemoria, con le prossime azioni delle trattative. Si collega a Google Calendar, Apple e Outlook. */
export function AgendaPage({ ctx }: { ctx: Ctx }) {
  const { api } = ctx;
  const [month, setMonth] = useState(thisMonth());
  const [selected, setSelected] = useState(todayKey());
  const [data, setData] = useState<AgendaResponse | null>(null);
  const [upcoming, setUpcoming] = useState<AgendaResponse | null>(null);
  const [editing, setEditing] = useState<EventDto | { day: string } | null>(null);
  const [linking, setLinking] = useState(false);
  const [visitId, setVisitId] = useState<number | null>(null);
  const [visitForm, setVisitForm] = useState<{ visit: import('../../../../shared/types').VisitDto | null } | null>(null);
  /** Gli eventi delle visite si aprono come visita */
  const openEvent = (e: EventDto) => (e.visitId ? setVisitId(e.visitId) : setEditing(e));
  const [error, setError] = useState<string | null>(null);
  // Chi usa lo stesso calendario anche per un altro lavoro (i commerciali) di base vede solo Tecnoside
  const showKey = `tt.agenda.show.${ctx.me.user.id}`;
  const [show, setShowState] = useState<Show>(() => {
    const v = localStorage.getItem(showKey);
    return v === 'tecnoside' || v === 'together' || v === 'all' ? v : ctx.me.user.role === 'agent' ? 'tecnoside' : 'all';
  });
  const setShow = (v: Show) => {
    localStorage.setItem(showKey, v);
    setShowState(v);
  };

  const load = useCallback(async () => {
    try {
      const r = monthRange(month);
      setData(await api.get<AgendaResponse>(`/api/agenda?from=${encodeURIComponent(r.from)}&to=${encodeURIComponent(r.to)}`));
      const now = new Date();
      const to = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 15);
      const from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      setUpcoming(await api.get<AgendaResponse>(`/api/agenda?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api, month]);

  useEffect(() => {
    void load();
  }, [load]);

  // Arrivo dall'avviso "collega Google Calendar": apro subito la finestra del collegamento
  useEffect(() => {
    if ((ctx.pageParam as { google?: boolean } | null)?.google) setLinking(true);
  }, [ctx.pageParam]);

  // Novità arrivate da Google Calendar
  useEffect(() => {
    if (ctx.event?.type === 'agenda.changed') void load();
  }, [ctx.eventSeq]); // eslint-disable-line react-hooks/exhaustive-deps

  const marks = useMemo(() => {
    // Un pallino = c'è un impegno; due pallini (il secondo verde) = c'è anche qualcosa da fare insieme a un collega
    // Il terzo pallino (rosa) = qualcuno l'ha messo nel calendario condiviso (es. Carmen)
    const m: Record<string, DayMark> = {};
    for (const e of data?.events ?? [])
      if (passes(e, show)) {
        const d = (m[eventDay(e)] ??= {});
        if (isTogether(e)) d.together = true;
        if (e.bookedBy) d.booked = true;
      }
    if (show !== 'together') for (const d of data?.dealActions ?? []) m[d.date] ??= {};
    return m;
  }, [data, show]);

  // Nome del collega con cui si fanno gli impegni insieme (per la legenda del calendario)
  // Chi prenota dal calendario condiviso (per la legenda): il nome di battesimo, se è sempre la stessa persona
  const bookers = [...new Set((data?.events ?? []).map((e) => e.bookedBy).filter(Boolean) as string[])];
  const booker = bookers.length === 1 ? bookers[0].split(' ')[0] : 'altri';
  const colleague = (data?.events ?? []).flatMap((e) => e.together ?? []).map((n) => n.split(' ')[0]).find(Boolean) ?? 'un collega';
  // Impegni di ogni giorno, per il suggerimento che compare passando sopra il giorno nel calendario
  const dayDetails = useMemo(() => {
    const d: Record<string, string[]> = {};
    for (const e of data?.events ?? []) if (passes(e, show)) (d[eventDay(e)] ??= []).push(`${e.allDay ? 'Tutto il giorno' : fmtTime(e.startsAt)} ${e.title}${isTogether(e) ? ' (con ' + e.together!.map((n) => n.split(' ')[0]).join(', ') + ')' : ''}`);
    if (show !== 'together') for (const a of data?.dealActions ?? []) (d[a.date] ??= []).push(`${a.name}${a.text ? ': ' + a.text : ''}`);
    return d;
  }, [data, show]);

  const ofDay = (data?.events ?? []).filter((e) => eventDay(e) === selected);
  const dayEvents = ofDay.filter((e) => passes(e, show));
  const dayHidden = ofDay.length - dayEvents.length;
  const dayDeals = show === 'together' ? [] : (data?.dealActions ?? []).filter((d) => d.date === selected);

  const toggleDone = async (e: EventDto) => {
    try {
      await api.put(`/api/agenda/${e.id}`, { ...e, done: !e.done });
      void load();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const { upList, upHidden, upTogether } = useMemo(() => {
    const items: { key: string; day: string; time: string; title: string; ev?: EventDto; deal?: AgendaDealAction }[] = [];
    let hidden = 0;
    let together = 0;
    for (const e of upcoming?.events ?? []) {
      if (e.done) continue;
      if (isTogether(e)) together++;
      if (!passes(e, show)) {
        hidden++;
        continue;
      }
      items.push({ key: `e${e.id}`, day: eventDay(e), time: e.allDay ? 'Tutto il giorno' : fmtTime(e.startsAt), title: e.title, ev: e });
    }
    if (show !== 'together')
      for (const d of upcoming?.dealActions ?? []) {
        items.push({ key: `d${d.dealId}`, day: d.date, time: 'Trattativa', title: `${d.text || 'Da seguire'} · ${d.name}`, deal: d });
      }
    return { upList: items.sort((a, b) => (a.day + a.time).localeCompare(b.day + b.time)), upHidden: hidden, upTogether: together };
  }, [upcoming, show]);

  const hiddenNote = (n: number) =>
    n > 0 && show !== 'all' ? (
      <button type="button" className="ag-hidden" onClick={() => setShow('all')}>
        <Icon name="eye" size={14} /> {n === 1 ? '1 altro impegno nascosto' : `${n} altri impegni nascosti`} · Mostra
      </button>
    ) : null;

  return (
    <div className="panels">
      <Panel
        title="Agenda"
        className="ag-panel"
        right={
          <div className="row-actions" style={{ margin: 0 }}>
            <button type="button" className="btn" title="Collega l’agenda al tuo Google Calendar, così la vedi anche sul telefono" onClick={() => setLinking(true)}>
              <Icon name="link" size={15} /> Google Calendar
            </button>
            <button type="button" className="btn" title="Aggiungi un appuntamento o una cosa da ricordare nel giorno scelto" onClick={() => setEditing({ day: selected })}>
              <Icon name="plus" size={16} /> Promemoria
            </button>
            <button type="button" className="btn dark" title="Fissa una visita o una call con un cliente" onClick={() => setVisitForm({ visit: null })}>
              <Icon name="mic" size={16} /> Nuova visita
            </button>
          </div>
        }
      >
        <ErrorBanner message={error} onClose={() => setError(null)} />
        <div className="ag-filter" role="tablist" aria-label="Quali appuntamenti vedere">
          {SHOWS.map((o) => (
            <button key={o.v} type="button" role="tab" aria-selected={show === o.v} className={show === o.v ? 'on' : ''} title={o.hint} onClick={() => setShow(o.v)}>
              {o.v === 'together' && <span className="ag-dot" />}
              {o.label}
            </button>
          ))}
        </div>
        <Calendar month={month} selected={selected} marks={marks} onSelect={setSelected} onMonth={setMonth} details={dayDetails} legend={[{ label: 'Impegno' }, { label: `Con ${colleague}`, together: true }, ...(bookers.length ? [{ label: `Prenotato da ${booker}`, booked: true }] : [])]} />

        <h3 style={{ margin: '18px 0 8px' }}>{dayLabel(selected)}</h3>
        {dayEvents.length === 0 && dayDeals.length === 0 && (
          <div className="muted small">{show === 'together' ? 'Niente da fare insieme.' : 'Niente in programma.'}</div>
        )}
        <div className="ag-list">
          {dayEvents.map((e) => (
            <EventRow key={e.id} e={e} onOpen={() => openEvent(e)} onToggle={() => void toggleDone(e)} />
          ))}
          {dayDeals.map((d) => (
            <div key={`d${d.dealId}`} className="ag-row deal" onClick={() => ctx.setPage('deals')}>
              <span className="ag-time">Trattativa</span>
              <span className="ag-title">
                {d.text || 'Da seguire'} · <span className="muted">{d.name}</span>
              </span>
            </div>
          ))}
          {hiddenNote(dayHidden)}
        </div>
      </Panel>

      <Panel
        title="Prossimi giorni"
        subtitle="Da oggi per due settimane"
        right={
          upTogether > 0 ? (
            <button type="button" className="pill good ag-together-pill" title="Mostra solo gli impegni da fare insieme" onClick={() => setShow('together')}>
              <span className="ag-dot" /> {upTogether} insieme
            </button>
          ) : undefined
        }
      >
        {upList.length === 0 ? (
          <>
            <Empty icon="calendar">{show === 'together' ? 'Niente da fare insieme nei prossimi giorni.' : 'Nessun impegno nei prossimi giorni.'}</Empty>
            {hiddenNote(upHidden)}
          </>
        ) : (
          <div className="ag-list">
            {groupByDay(upList).map(([day, items], gi) => (
              <section key={day} className="ag-group" style={{ '--day-color': DAY_COLORS[gi % DAY_COLORS.length] } as React.CSSProperties}>
                <div className="ag-group-head">
                  <b>{dayLabel(day)}</b>
                  {day === todayKey() && <span className="ag-group-today">Oggi</span>}
                  <span className="muted small">{items.length === 1 ? '1 impegno' : `${items.length} impegni`}</span>
                </div>
                {items.map((it) => (
                  <div
                    key={it.key}
                    className={`ag-row${it.deal ? ' deal' : ''}${it.ev ? rowClass(it.ev) : ''}`}
                    onClick={() => {
                      if (it.ev) openEvent(it.ev);
                      else ctx.setPage('deals');
                    }}
                  >
                    <span className="ag-time">{it.time}</span>
                    <span className="ag-title">{it.title}</span>
                    {it.ev && <EventTags e={it.ev} />}
                  </div>
                ))}
              </section>
            ))}
            {hiddenNote(upHidden)}
          </div>
        )}
      </Panel>

      {editing && (
        <EventModal
          ctx={ctx}
          event={'id' in editing ? editing : null}
          day={'day' in editing ? editing.day : selected}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
      {linking && <LinkModal ctx={ctx} onClose={() => setLinking(false)} />}
      {visitForm && (
        <VisitFormModal
          ctx={ctx}
          visit={visitForm.visit}
          day={selected}
          onClose={() => setVisitForm(null)}
          onSaved={(v) => {
            setVisitForm(null);
            setVisitId(v.id);
            void load();
          }}
        />
      )}
      {visitId !== null && !visitForm && (
        <VisitDetailModal ctx={ctx} id={visitId} onClose={() => setVisitId(null)} onEdit={(v) => setVisitForm({ visit: v })} onChanged={() => void load()} />
      )}
    </div>
  );
}

/** Insieme a un collega: verde. Altro lavoro (visibile solo con «Tutti»): attenuato. */
const rowClass = (e: EventDto) => (isTogether(e) ? ' together' : !isTecno(e) ? ' other' : '');

/** Etichetta a destra: con chi è l'appuntamento, oppure «Altro lavoro». */
function EventTags({ e }: { e: EventDto }) {
  if (e.bookedBy && !isTogether(e))
    return (
      <span className="ag-tag booked" title="Messo da questa persona nel calendario condiviso">
        Da {e.bookedBy.split(' ')[0]}
      </span>
    );
  if (isTogether(e))
    return (
      <span className="ag-tag together" title="Da fare insieme">
        <Icon name="users" size={12} /> Con {e.together!.join(', ')}
      </span>
    );
  if (!isTecno(e))
    return (
      <span className="ag-tag other" title="Non riguarda un cliente Tecnoside">
        Altro lavoro
      </span>
    );
  return null;
}

function EventRow({ e, onOpen, onToggle }: { e: EventDto; onOpen: () => void; onToggle: () => void }) {
  const end = e.endsAt && !e.allDay ? ` – ${fmtTime(e.endsAt)}` : '';
  return (
    <div className={`ag-row${e.done ? ' done' : ''}${e.visitId ? ' visit' : ''}${rowClass(e)}`} onClick={onOpen}>
      <button
        type="button"
        className="ag-check"
        title={e.done ? 'Segna come da fare' : 'Segna come fatto'}
        onClick={(ev) => {
          ev.stopPropagation();
          onToggle();
        }}
      >
        <Icon name={e.done ? 'check' : 'circle'} size={16} />
      </button>
      <span className="ag-time">{e.allDay ? 'Tutto il giorno' : fmtTime(e.startsAt) + end}</span>
      <span className="ag-title">
        {e.visitId && <Icon name="mic" size={13} className="muted" />} {e.title}
        {e.location && <span className="muted"> · {e.location}</span>}
      </span>
      <EventTags e={e} />
      {e.remindMin !== null && <Icon name="bell" size={13} className="muted" />}
    </div>
  );
}

// ---------------------------------------------------------------------------

function EventModal({ ctx, event, day, onClose, onSaved }: { ctx: Ctx; event: EventDto | null; day: string; onClose: () => void; onSaved: () => void }) {
  const { api } = ctx;
  const initStart = event && !event.allDay ? new Date(event.startsAt) : null;
  const initEnd = event?.endsAt && !event.allDay ? new Date(event.endsAt) : null;
  const hm = (d: Date | null, fallback: string) => (d ? `${pad(d.getHours())}:${pad(d.getMinutes())}` : fallback);

  const [title, setTitle] = useState(event?.title ?? '');
  const [date, setDate] = useState(event ? (event.allDay ? event.startsAt.slice(0, 10) : localDay(event.startsAt)) : day);
  const [allDay, setAllDay] = useState(event?.allDay ?? false);
  const cal = useBusy(api, [ctx.me.user.id], null, date);
  const [start, setStart] = useState(hm(initStart, ''));
  const [end, setEnd] = useState(hm(initEnd, ''));
  const [location, setLocation] = useState(event?.location ?? '');
  const [notes, setNotes] = useState(event?.notes ?? '');
  const [remind, setRemind] = useState<number | null>(event ? event.remindMin : 10);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const build = (): Omit<EventDto, 'id'> | null => {
    if (!title.trim()) {
      setError('Indica il titolo');
      return null;
    }
    let startsAt: string;
    let endsAt: string | null = null;
    if (allDay) {
      startsAt = `${date}T00:00:00.000Z`;
    } else {
      if (!start) {
        setError("Scegli l'orario");
        return null;
      }
      const s = new Date(`${date}T${start}`);
      if (Number.isNaN(s.getTime())) {
        setError("Ora d'inizio non valida");
        return null;
      }
      startsAt = s.toISOString();
      if (end) {
        const e = new Date(`${date}T${end}`);
        if (e <= s) {
          setError("L'ora di fine deve essere dopo l'inizio");
          return null;
        }
        endsAt = e.toISOString();
      }
    }
    return {
      title: title.trim(),
      notes: notes.trim() || null,
      location: location.trim() || null,
      startsAt,
      endsAt,
      allDay,
      remindMin: allDay ? null : remind,
      done: event?.done ?? false,
      dealId: event?.dealId ?? null,
    };
  };

  const save = async () => {
    const body = build();
    if (!body) return;
    setBusy(true);
    try {
      if (event) await api.put(`/api/agenda/${event.id}`, body);
      else await api.post('/api/agenda', body);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!event || !(await ask(`Eliminare "${event.title}"?`))) return;
    try {
      await api.del(`/api/agenda/${event.id}`);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Modal
      title={event ? 'Appuntamento' : 'Nuovo appuntamento'}
      onClose={onClose}
      footer={
        <div className="quote-actions">
          {event && (
            <>
              <button type="button" className="btn ghost" onClick={() => void remove()} disabled={busy} title="Elimina l’appuntamento">
                <Icon name="trash" size={15} />
              </button>
              <a className="btn" href={googleLink(event)} target="_blank" rel="noreferrer" title="Aggiungilo al tuo Google Calendar">
                <Icon name="calendar" size={15} /> Google Calendar
              </a>
            </>
          )}
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          <button type="button" className="btn dark" onClick={() => void save()} disabled={busy}>
            {busy ? 'Salvataggio…' : 'Salva'}
          </button>
        </div>
      }
    >
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {event && isTogether(event) && (
        <p className="ag-note together">
          <Icon name="users" size={14} /> Da fare insieme a <b>{event.together!.join(', ')}</b>: c'è anche nella sua agenda.
        </p>
      )}
      {event && !isTecno(event) && (
        <p className="ag-note" title="Di solito gli impegni di altro lavoro restano nascosti. Il nome del cliente va scritto come è in rubrica.">
          È segnato come <b>altro lavoro</b>: se è di Tecnoside, scrivi nel titolo il cliente o «Tecnoside».
        </p>
      )}
      <Field label="Titolo">
        <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus={!event} placeholder="Chiamata con…, richiamare…" />
      </Field>
      <div className="fields-3">
        <Field label="Data">
          <DateInput value={date} onChange={setDate} busy={cal.count} onMonth={cal.showMonth} />
        </Field>
        {!allDay && (
          <>
            <Field label="Inizio">
              <TimeInput
                value={start}
                onChange={(v) => {
                  setStart(v);
                  if (!end || end <= v) setEnd(addMin(v, 60));
                }}
              />
            </Field>
            <Field label="Fine (facoltativa)">
              <TimeInput value={end} onChange={setEnd} />
            </Field>
          </>
        )}
      </div>
      {!allDay && (
        <SlotPicker
          day={date}
          busy={cal.onDay(date).filter((b) => b.id !== event?.id)}
          start={start}
          end={end}
          onDay={setDate}
          onPick={(s, e) => {
            setStart(s);
            setEnd(e);
          }}
        />
      )}
      <label className="ag-allday" style={{ height: 'auto', marginTop: -6, marginBottom: 14 }}>
        <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} /> Tutto il giorno
      </label>
      <More open={!!(location || notes || (event && !allDay && remind !== 10))}>
        <div className="fields-2">
          {!allDay && (
            <Field label="Promemoria">
              <select value={remind ?? ''} onChange={(e) => setRemind(e.target.value === '' ? null : Number(e.target.value))}>
                {REMINDERS.map((r) => (
                  <option key={String(r.v)} value={r.v ?? ''}>
                    {r.label}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Luogo / link">
            <input value={location} onChange={(e) => setLocation(e.target.value)} />
          </Field>
        </div>
        <Field label="Note">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
        </Field>
      </More>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

function LinkModal({ ctx, onClose }: { ctx: Ctx; onClose: () => void }) {
  const { api } = ctx;
  const [g, setG] = useState<GoogleStatusDto | null>(null);
  const [waiting, setWaiting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showFeed, setShowFeed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      const s = await api.get<GoogleStatusDto>('/api/agenda/google');
      setG(s);
      return s;
    } catch (e) {
      setError((e as Error).message);
      return null;
    }
  }, [api]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  // Dopo aver aperto la pagina di Google: controllo finché il collegamento non risulta fatto
  useEffect(() => {
    if (!waiting) return;
    const t = setInterval(() => {
      void loadStatus().then((s) => {
        if (s?.connected && !s.error) setWaiting(false);
      });
    }, 3000);
    const stop = setTimeout(() => setWaiting(false), 5 * 60_000);
    return () => {
      clearInterval(t);
      clearTimeout(stop);
    };
  }, [waiting, loadStatus]);

  const connect = async () => {
    setError(null);
    try {
      const { url } = await api.post<{ url: string }>('/api/agenda/google/connect');
      window.open(url, '_blank', 'noopener');
      setWaiting(true);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      setG(await api.post<GoogleStatusDto>('/api/agenda/google/sync'));
    } catch (e) {
      setError((e as Error).message);
    }
    setBusy(false);
  };

  const unlink = async () => {
    if (!(await ask('Scollegare Google Calendar? Gli appuntamenti restano in agenda, ma non si aggiorneranno più con Google.'))) return;
    setBusy(true);
    try {
      setG(await api.del<GoogleStatusDto>('/api/agenda/google'));
    } catch (e) {
      setError((e as Error).message);
    }
    setBusy(false);
  };

  return (
    <Modal
      title="Google Calendar"
      onClose={onClose}
      footer={
        <div className="quote-actions">
          <button type="button" className="btn" onClick={onClose}>
            Chiudi
          </button>
        </div>
      }
    >
      <ErrorBanner message={error ?? g?.error ?? null} onClose={() => setError(null)} />
      {!g ? (
        <div className="muted small">Caricamento…</div>
      ) : g.connected ? (
        <>
          <p className="small" title="Funziona nei due sensi, anche dal telefono.">
            <Icon name="check" size={14} /> Collegato a <b>{g.email ?? 'Google'}</b>: quello che crei qui o su Google compare in tutti e due entro un minuto.
          </p>
          {g.lastSyncAt && <p className="muted small">Ultimo aggiornamento: {fmtTime(g.lastSyncAt)}</p>}
          <div className="row-actions">
            <button type="button" className="btn dark" onClick={() => void syncNow()} disabled={busy} title="Prende subito le novità da Google, senza aspettare">
              <Icon name="refresh" size={14} /> {busy ? 'Aggiorno…' : 'Aggiorna ora'}
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => void connect()}
              disabled={busy || waiting}
              title="Rinnova l'accesso a Google senza toccare gli appuntamenti (serve se compare «accesso scaduto»)"
            >
              <Icon name="link" size={14} /> {waiting ? 'In attesa di Google…' : 'Ricollega'}
            </button>
            <button type="button" className="btn ghost" onClick={() => void unlink()} disabled={busy} title="Smette di aggiornarsi con Google: gli appuntamenti restano qui">
              Scollega
            </button>
          </div>
          {waiting && <p className="muted small">Si è aperta la pagina di Google: scegli il tuo account e consenti l'accesso al calendario.</p>}
        </>
      ) : !g.available ? (
        <p className="small">Il collegamento con Google non è ancora pronto: lo attiva l’amministratore.</p>
      ) : (
        <>
          <p className="small">
            Collega il tuo <b>Google Calendar</b>: gli appuntamenti si aggiornano nei due sensi, anche dal telefono.
          </p>
          <button type="button" className="btn dark" onClick={() => void connect()} disabled={waiting}>
            <Icon name="calendar" size={15} /> {waiting ? 'In attesa di Google…' : 'Collega Google Calendar'}
          </button>
          {waiting && <p className="muted small">Si è aperta la pagina di Google nel browser: scegli il tuo account e consenti l'accesso al calendario.</p>}
        </>
      )}
      <div style={{ marginTop: 18 }}>
        <button type="button" className="btn ghost" title="Per vedere l’agenda anche nel calendario di Apple o di Outlook" onClick={() => setShowFeed((v) => !v)}>
          <Icon name="link" size={14} /> Apple Calendario o Outlook
        </button>
      </div>
      {showFeed && <FeedLink ctx={ctx} />}
    </Modal>
  );
}

/** Indirizzo in abbonamento (un solo senso) per Apple Calendario e Outlook. */
function FeedLink({ ctx }: { ctx: Ctx }) {
  const { api } = ctx;
  const [url, setUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ path: string }>('/api/agenda/feed-url')
      .then((r) => setUrl(api.baseUrl + r.path))
      .catch((e) => setError((e as Error).message));
  }, [api]);

  const regenerate = async () => {
    if (!(await ask('Il vecchio indirizzo smetterà di funzionare: dovrai incollare quello nuovo nel tuo calendario. Continuare?'))) return;
    try {
      setUrl(api.baseUrl + (await api.post<{ path: string }>('/api/agenda/feed-url/regenerate')).path);
      setCopied(false);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const copy = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url).catch(() => undefined);
    setCopied(true);
  };

  return (
    <div style={{ marginTop: 10 }}>
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <Field label="Indirizzo del tuo calendario" hint="Personale: chi lo ha può leggere la tua agenda. Non condividerlo.">
        <div className="row-actions" style={{ margin: 0 }}>
          <input readOnly value={url ?? 'Caricamento…'} onFocus={(e) => e.target.select()} style={{ flex: 1 }} />
          <button type="button" className="btn dark" onClick={() => void copy()} disabled={!url}>
            <Icon name="copy" size={15} /> {copied ? 'Copiato' : 'Copia'}
          </button>
        </div>
      </Field>
      <ol className="small ag-steps">
        <li>
          <b>Apple Calendario</b>: File → Nuova iscrizione a calendario → incolla.
        </li>
        <li>
          <b>Outlook</b>: Aggiungi calendario → Sottoscrivi da web → incolla.
        </li>
      </ol>
      <p className="muted small">Questo va in un solo senso: dall'agenda al calendario.</p>
      <button type="button" className="btn ghost" title="Se l’indirizzo è finito in mani sbagliate: quello vecchio smette di funzionare" onClick={() => void regenerate()}>
        <Icon name="refresh" size={14} /> Genera un nuovo indirizzo
      </button>
    </div>
  );
}
